// Copyright (c) Bentley Systems
import {
ActionColumn,
Button,
ComboBox,
DefaultCell,
DropdownMenu,
  IconButton,
  InformationPanel,
  InformationPanelBody,
  InformationPanelContent,
  InformationPanelHeader,
  InformationPanelWrapper,
  MenuItem,
  ProgressRadial,
  SearchBox,
  Table,
  tableFilters,
  TablePaginator,
  TablePaginatorRendererProps,
  Text,
} from "@itwin/itwinui-react";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAccessToken } from "../../app/authorization-slice";
import {
  customCellStringSort,
  FormatDate,
  getParametersFromUrlString,
  getQueryParam,
  isGuid,
  setErrorState,
} from "../../Helpers/helper-functions";
import { useElementHeights } from "../../Helpers/useElementHeights";
import { useNodePermission } from "../../Helpers/useNodePermission";
import {
  selectMainMenuHeightWithMargin,
  selectSideMenuWidthWithMargin,
} from "../../app/header-slice";
import {
  selectConfirmation,
  setConfirmation,
} from "../../app/confirmation-slice";
import CredentialsForm from "../CredentialsForm/CredentialsForm";
import { mapDataToModels } from "../../Helpers/map-response";
import { SystemInList } from "./Models/system-in-list";
import { SvgDelete, SvgEdit, SvgMore, SvgProgressBackward } from "@itwin/itwinui-icons-react";
import { appendLoadMoreOption, LOAD_MORE_VALUE, paginatedItemRenderer, searchPlaceholder } from "../../Helpers/ComboBoxHelpers";
import IconByName from "../../Icons/IconByName";
import {
  CellProps,
  CellRendererProps,
  Column,
} from "@itwin/itwinui-react/react-table";
import { OnPremise } from "./Models/on-premise";
import { useToastNotificationService } from "../../Helpers/ToastNotificationService";
import { setformOpen } from "../../app/form-open-slice";
import OnPremSystemForm from "../OnPremSystemForm/OnPremSystemForm";
import OnPremForm from "../OnPremiseForm/OnPremForm";
import { DeleteCredentialType } from "./Models/delete-credential-type";
import { OnPremiseSystem } from "./Models/on-premise-system";
import { ConfigManager } from "../../Helpers/config-manager";
import { DawComponent } from "../../Helpers/daw-component";
import { useITwinProjects } from "../../Helpers/useITwinProjects";
import { ITwin } from "../../SubscribedProjects/Models/itwin";
import { useITwinOnPremTelemetryApi } from "../../OnPremTelemetry/useITwinOnPremTelemetryApi";
import OnPremClientBlock, { ON_PREM_HEALTH_WINDOW } from "./OnPremClientBlock";

export enum CredentialFormState {
  Cloud = "Cloud",
  OnPremClient = "OnPremClient",
  OnPremSystem = "OnPremSystem",
}

const CredentialsList = () => {
  const iTwinQueryParam = getQueryParam("iTwinId");
  const { isNodeAllowed: isBdpiNodeAllowed } = useNodePermission(
    DawComponent.DAW_COMPONENT_CREDENTIAL_LIST,
    {
      extraHeaders: iTwinQueryParam
        ? { "Itwin-ID": iTwinQueryParam }
        : undefined,
    },
  );
  const headerDivRef = useRef<HTMLDivElement>(null);
  const headerButtonsDivRef = useRef<HTMLDivElement>(null);
  const variantButtonsDivRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { showToast } = useToastNotificationService();
  const mainMenuHeight = useAppSelector(selectMainMenuHeightWithMargin);
  const [isOnPremVariantActive, setIsOnPremVariantActive] =
    useState<boolean>(false);
  const [formState, setFormState] = useState<CredentialFormState>(
    CredentialFormState.Cloud,
  );
  const [systems, setSystems] = useState<SystemInList[]>([]);
  const [selectedSystem, setSelectedSystem] = useState<SystemInList>();
  const [selectedOnPrem, setSelectedOnPrem] = useState<OnPremise>();
  const [onPremises, setOnPremises] = useState<OnPremise[]>([]);
  const [searchString, setSearchString] = useState<string>();
  const [actionCount, setActionCount] = useState<number>(0);
  const [isLoading, setLoading] = useState<boolean>(false);
  const { params } = useParams();
  const accessToken = useAppSelector(selectAccessToken);
  const [fRollUrl, setFrollUrl] = useState<string>();
  const [infoPanelHeader, setInfoPanelHeader] = useState<string>();
  const urlParameters = useMemo(
    () => getParametersFromUrlString(params),
    [params],
  );
  const sideMenuWidth = useAppSelector(selectSideMenuWidthWithMargin);
  const confirmationState = useAppSelector(selectConfirmation);

  const projectSelectorDivRef = useRef<HTMLDivElement>(null);
  const [
    headerDivHeight,
    headerButtonsDivHeight,
    variantButtonsDivHeight,
    projectSelectorDivHeight,
  ] = useElementHeights(
    headerDivRef,
    headerButtonsDivRef,
    variantButtonsDivRef,
    projectSelectorDivRef,
  );

  // Use the iTwin projects hook
  const {
    projects,
    selectedProject,
    isProjectsLoading,
    nextLink,
    fetchNextProjects,
    handleSearchChange: projectSearchOnChange,
    handleProjectSelect,
    selectOptions,
    emptyContent,
  } = useITwinProjects({
    isEnabled: isBdpiNodeAllowed && !iTwinQueryParam,
    accessToken,
    dispatch,
  });



  // Compute iTwinId from query param first
  const iTwinIdFromQuery = useMemo(() => {
    const iTwinIdPar = urlParameters.find((p) => p.name === "iTwinId");
    const value = iTwinIdPar?.value;
    if (value && !isGuid(value)) {
      return undefined;
    }
    return value;
  }, [urlParameters]);

  const effectiveITwinId = useMemo(
    () => iTwinIdFromQuery || selectedProject?.id,
    [iTwinIdFromQuery, selectedProject]
  );

  // Use effectiveITwinId instead of iTwinId throughout the component
  const iTwinId = effectiveITwinId;
  // Determine if opened in standalone mode (without query param)
  const isStandaloneMode = !iTwinIdFromQuery;

  const { telemetryByClientName, isAvailable: isOnPremTelemetryAvailable } = useITwinOnPremTelemetryApi({
    accessToken,
    iTwinId,
    isEnabled: isBdpiNodeAllowed && isOnPremVariantActive,
    dayFilter: ON_PREM_HEALTH_WINDOW,
    reloadKey: actionCount,
  });

  const iModelDesc = useMemo(() => {
    const iModelDescPar = urlParameters.find((p) => p.name === "IMODEL_DESC");
    const itName = iModelDescPar?.value;
    return itName;
  }, [urlParameters]);

  function navigateToInfPanelFunction(navFrolUrl: string) {
    setInfoPanelHeader(undefined);
    setFrollUrl(navFrolUrl);
  }

  function onFrollClose(refreshList: boolean) {
    setFrollUrl(undefined);
    setSelectedSystem(undefined);
    setSelectedOnPrem(undefined);
    setInfoPanelHeader(undefined);
    if (refreshList) {
      refreshListFunction();
    }
  }

  function onSetInfoPanelHeader(header: string) {
    setInfoPanelHeader(header);
  }

  function refreshListFunction() {
    setActionCount(actionCount + 1);
  }

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setFrollUrl(undefined);
      setInfoPanelHeader(undefined);
      if (isBdpiNodeAllowed) {
        if (!iTwinId) {
          // In standalone mode it's expected that iTwinId is undefined until user selects a project so only show error in trad mode (when iTwinId should come from query params)
          if (!isStandaloneMode) {
            showToast({ status: "negative", message: "iTwinId is not defined" });
          }
          return;
        }
        //cloud
        if (!isOnPremVariantActive) {
          setLoading(true);
          let path = `/edfs/itwins/${encodeURIComponent(iTwinId)}/systems`;
          // Remove /edfs from the url if the edfsUrl is different from iTwinPlatformUrl (R&D)
          path = ConfigManager.removeEdfsIfNeeded(path);
          const url = ConfigManager.edfsUrl + path;
          const runIdStructure = crypto.randomUUID();
          fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: accessToken,
              RunId: runIdStructure,
            },
            signal,
          })
            .then(async function (response) {
              if (!response.ok) {
                setErrorState(response.status, dispatch, runIdStructure, true);
                return null;
              }
              return response.json();
            })
            .then(async function (data) {
              if (!data) {
                return;
              }
              const systemsInList: SystemInList[] =
                mapDataToModels<SystemInList>(data.systems);
              systemsInList.forEach((s) => {
                s.lastChange = new Date(s.lastChange);
              });
              setSystems(systemsInList);
            })
            .catch((error) => {
              if (error.name !== "AbortError") {
                setErrorState(500, dispatch, runIdStructure, true);
              }
            })
            .finally(() => setLoading(false));
        }
        //onPremises
        else {
          setLoading(true);
          const path = `/itwins/${encodeURIComponent(iTwinId)}/onpremises`;
          const url = ConfigManager.edfsItwinInternalUrl + path;
          const runIdStructure = crypto.randomUUID();
          fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: accessToken,
              RunId: runIdStructure,
            },
            signal,
          })
            .then(async function (response) {
              if (!response.ok) {
                setErrorState(response.status, dispatch, runIdStructure, true);
                return null;
              }
              return response.json();
            })
            .then(async function (data) {
              if (!data) {
                return;
              }
              const onPremisesModels: OnPremise[] = mapDataToModels<OnPremise>(
                data.onPremises,
              );

              setOnPremises(onPremisesModels);
            })
            .catch((error) => {
              if (error.name !== "AbortError") {
                setErrorState(500, dispatch, runIdStructure, true);
              }
            })
            .finally(() => setLoading(false));
        }
      }
    },
    [accessToken, dispatch, iTwinId, isOnPremVariantActive, isBdpiNodeAllowed],
  );



  useEffect(() => {
    const abortController = new AbortController();
    void load(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [urlParameters, actionCount, load]);

  useEffect(() => {
    if (fRollUrl === undefined) {
      dispatch(setformOpen({ isOpen: false }));
    } else {
      dispatch(setformOpen({ isOpen: true }));
    }
  }, [dispatch, fRollUrl]);

  const deleteCredential = useCallback(
    async (
      id: string,
      deleteType: DeleteCredentialType,
      onPremiseId: string | undefined = undefined,
    ): Promise<boolean> => {
      setLoading(true);
      let result = true;
      let deleteUrl = "";
      const iTwinSegment = encodeURIComponent(iTwinId ?? "");
      switch (deleteType) {
        case DeleteCredentialType.CloudSystem: {
          deleteUrl = ConfigManager.edfsUrl;
          let path = `/edfs/itwins/${iTwinSegment}/systems/${encodeURIComponent(id)}`;
          // Remove /edfs from the url if the edfsUrl is different from iTwinPlatformUrl (R&D)
          path = ConfigManager.removeEdfsIfNeeded(path);
          deleteUrl += path;
          break;
        }
        case DeleteCredentialType.OnPremise: {
          deleteUrl =
            `${ConfigManager.edfsItwinInternalUrl}/itwins/${iTwinSegment}/onpremises/${encodeURIComponent(id)}`;
          break;
        }
        case DeleteCredentialType.OnPremiseSystem: {
          deleteUrl =
            `${ConfigManager.edfsItwinInternalUrl}/itwins/${iTwinSegment}/onpremises/${encodeURIComponent(onPremiseId ?? "")}/systems/${encodeURIComponent(id)}`;
          break;
        }
      }

      const runIdStructure = crypto.randomUUID();
      await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runIdStructure,
        },
      })
        .then(function (response) {
          if (!response.ok) {
            setErrorState(response.status, dispatch, runIdStructure, true);
            result = false;
          }
        })
        .finally(() => setLoading(false));

      return result;
    },
    [accessToken, dispatch, iTwinId],
  );

  const onDeleteCloudSystemClick = useCallback(
    async (systemId: string) => {
      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          onOkAction: async () => {
            const deleted = await deleteCredential(
              systemId,
              DeleteCredentialType.CloudSystem,
            );
            if (deleted) {
              void load();
            }
          },
          caption: "Delete confirmation",
          text: "Are you sure you want to delete this connection?",
          okText: "Yes",
          cancelText: "No",
        }),
      );
    },
    [deleteCredential, dispatch, load],
  );

  const onDeleteOnPremiseSystemClick = useCallback(
    async (onPremiseSystem: OnPremiseSystem) => {
      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          onOkAction: async () => {
            const deleted = await deleteCredential(
              onPremiseSystem.id,
              DeleteCredentialType.OnPremiseSystem,
              onPremiseSystem.onPremise.id,
            );
            if (deleted) {
              void load();
            }
          },
          caption: "Delete confirmation",
          text: "Are you sure you want to delete this on premise system?",
          okText: "Yes",
          cancelText: "No",
        }),
      );
    },
    [deleteCredential, dispatch, load],
  );

  const buttonCellTemplate = useCallback(
    (cellProps: CellProps<any>) => {
      const menuItems = (close: () => void) => {
        const menuItemStyle = {
          height: "1rem",
          width: "1rem",
        };
        const deleteMenuItem = (
          <MenuItem
            key="miDelete"
            startIcon={<IconByName name={"delete"} style={menuItemStyle} />}
            onClick={() => {
              close();
              if (isOnPremVariantActive) {
                void onDeleteOnPremiseSystemClick(cellProps.row.original.system);
              } else {
                void onDeleteCloudSystemClick(cellProps.row.original.system.id);
              }
            }}
          >
            Delete
          </MenuItem>
        );
        if (isOnPremVariantActive) {
          return [deleteMenuItem];
        } else {
          return [
            <MenuItem
              key="miEdit"
              startIcon={<IconByName name={"edit"} style={menuItemStyle} />}
              onClick={() => {
                close();
                if (isOnPremVariantActive) {
                  setFormState(CredentialFormState.OnPremSystem);
                  setSelectedOnPrem(cellProps.row.original.system.onPremise);
                } else {
                  setFormState(CredentialFormState.Cloud);
                  setSelectedSystem(cellProps.row.original.system);
                }
                setFrollUrl("credentials");
              }}
            >
              Edit
            </MenuItem>,
            deleteMenuItem,
          ];
        }
      };
      return (
        <>
          <DropdownMenu menuItems={menuItems}>
            <IconButton
              styleType="borderless"
              onClick={(e) => e.stopPropagation()}
              label="Actions"
            >
              <SvgMore />
            </IconButton>
          </DropdownMenu>
          <div className="becs-table-cell-shadow-left-elist" />
        </>
      );
    },
    [onDeleteCloudSystemClick, onDeleteOnPremiseSystemClick],
  );

  const columnsForCloud = useMemo(() => {
    const cols: Column<Record<string, unknown>>[] = [
      {
        Header: "User connection name",
        accessor: "systemName",
        id: "systemName",
        minWidth: 250,
        Filter: tableFilters.TextFilter(),
      },
      {
        Header: "System type",
        accessor: "systemType",
        id: "systemType",
        minWidth: 200,
        Filter: tableFilters.TextFilter(),
      },
      {
        Header: "System ID",
        accessor: "systemId",
        id: "systemId",
        minWidth: 250,
        Filter: tableFilters.TextFilter(),
      },
      {
        Header: "iTwinId",
        accessor: "iTwinId",
        id: "iTwinId",
        minWidth: 300,
        Filter: tableFilters.TextFilter(),
      },
      {
        Header: "Last change",
        accessor: "lastChange",
        id: "lastChange",
        minWidth: 200,
        sortType: customCellStringSort,
        Filter: tableFilters.DateRangeFilter({
          formatDate(date) {
            return date.toLocaleString();
          },
        }),
        filter: "betweenDate",
        cellRenderer: (props: CellRendererProps<any>) => {
          return (
            <DefaultCell {...props}>
              {FormatDate(props.cellProps.value)}
            </DefaultCell>
          );
        },
      },
      {
        Header: "Last change by",
        accessor: "lastChangeByName",
        id: "lastChangeByName",
        minWidth: 200,
        Filter: tableFilters.TextFilter(),
      },
      {
        ...ActionColumn({ columnManager: true }),
        Cell: buttonCellTemplate,
        sticky: "right",
      },
    ];
    return cols;
  }, [buttonCellTemplate]);

  const columnsForOnPremSystems = useMemo(() => {
    const cols: Column<Record<string, unknown>>[] = [
      {
        Header: "System name",
        accessor: "name",
        id: "name",
        minWidth: 200,
        Filter: tableFilters.TextFilter(),
      },
      {
        Header: "System type",
        accessor: "systemType",
        id: "systemType",
        minWidth: 200,
        Filter: tableFilters.TextFilter(),
      },
      {
        Header: "System ID",
        accessor: "systemId",
        id: "systemId",
        minWidth: 200,
        Filter: tableFilters.TextFilter(),
      },
      {
        Header: "Last change",
        accessor: "lastChange",
        id: "lastChange",
        minWidth: 200,
        sortType: customCellStringSort,
        Filter: tableFilters.DateRangeFilter({
          formatDate(date) {
            return date.toLocaleString();
          },
        }),
        filter: "betweenDate",
        cellRenderer: (props: CellRendererProps<any>) => {
          return (
            <DefaultCell {...props}>
              {FormatDate(props.cellProps.value)}
            </DefaultCell>
          );
        },
      },
      {
        Header: "Last change by",
        accessor: "lastChangeByName",
        id: "lastChangeByName",
        minWidth: 200,
        Filter: tableFilters.TextFilter(),
      },
      {
        ...ActionColumn({ columnManager: true }),
        Cell: buttonCellTemplate,
        sticky: "right",
      },
    ];
    return cols;
  }, [buttonCellTemplate]);

  const cloudSystemsData = useMemo(() => {
    const returnValues: Record<string, unknown>[] = [];
    systems.forEach(function (system) {
      returnValues.push(getRecord());
      function getRecord(): Record<string, unknown> {
        const returnRecord: Record<string, unknown> = {};
        returnRecord.systemName = system.systemName;
        returnRecord.systemType = system.systemType;
        returnRecord.systemId = system.systemId;
        returnRecord.iTwinId = system.iTwinId;
        returnRecord.lastChange = system.lastChange;
        returnRecord.lastChangeByName = system.lastChangeByName;
        returnRecord.system = system;

        return returnRecord;
      }
    });
    if (searchString !== undefined && searchString !== "") {
      const filteredData = returnValues.filter(
        (r) =>
          (r.systemName as string).toLowerCase().indexOf(searchString) > -1,
      );
      return filteredData;
    } else {
      return returnValues;
    }
  }, [systems, searchString]);

  const onPremSystemsData = useCallback(
    (opPrem: OnPremise) => {
      opPrem.systems.forEach((s) => (s.onPremise = opPrem));
      const returnValues: Record<string, unknown>[] = [];
      opPrem.systems.forEach(function (sys) {
        returnValues.push(getRecord());
        function getRecord(): Record<string, unknown> {
          const returnRecord: Record<string, unknown> = {};
          returnRecord.name = sys.name;
          returnRecord.systemType = sys.systemType;
          returnRecord.systemId = sys.systemId;
          returnRecord.lastChange = sys.lastChange;
          returnRecord.lastChangeByName = sys.lastChangeByName;
          returnRecord.system = sys;

          return returnRecord;
        }
      });
      if (searchString !== undefined && searchString !== "") {
        const filteredData = returnValues.filter(
          (r) => (r.name as string).toLowerCase().indexOf(searchString) > -1,
        );
        return filteredData;
      } else {
        return returnValues;
      }
    },
    [onPremises, searchString],
  );

  const pageSizeList = useMemo(() => [10, 25, 50, 100], []);
  const paginator = useCallback(
    (props: TablePaginatorRendererProps) => (
      <TablePaginator
        {...props}
        pageSizeList={pageSizeList}
        isLoading={false}
      />
    ),
    [pageSizeList],
  );

  const getParamsFromFrollNavUrl = useMemo(() => {
    if (fRollUrl === undefined) {
      return undefined;
    } else {
      const hasParams = fRollUrl.indexOf("=") !== -1;
      const paramSubstring = hasParams
        ? fRollUrl.substring(fRollUrl.lastIndexOf("/") + 1)
        : undefined;
      return paramSubstring;
    }
  }, [fRollUrl]);

  function getZindex(): number {
    return confirmationState.isConfirmationOpen ? 0 : 2;
  }

  const navigate = useNavigate();

  const searchBoxOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const searchStr = event.currentTarget.value.toLowerCase();
      setSearchString(searchStr);
    },
    [],
  );

  const onEditOnPremClick = useCallback(
    (onPrem: OnPremise, close: () => void) => () => {
      setFormState(CredentialFormState.OnPremClient);
      setSelectedOnPrem(onPrem);
      setFrollUrl("credentials");
      close();
    },
    [],
  );

  const onDeleteOnPremClick = useCallback(
    async (onPrem: OnPremise, close: () => void) => {
      close();

      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          onOkAction: async () => {
            const deleted = await deleteCredential(
              onPrem.id,
              DeleteCredentialType.OnPremise,
            );
            if (deleted) {
              void load();
            }
          },
          caption: "Delete confirmation",
          text: "Are you sure you want to delete this on premise?",
          okText: "Yes",
          cancelText: "No",
        }),
      );
    },
    [deleteCredential, dispatch, load],
  );

  const dropdownOnPremMenuItems = useCallback(
    (onPrem: OnPremise) => (close: () => void) => [
      <MenuItem
        key={1}
        onClick={onEditOnPremClick(onPrem, close)}
        startIcon={<SvgEdit />}
      >
        Edit
      </MenuItem>,
      <MenuItem
        key={2}
        onClick={() => {
          void onDeleteOnPremClick(onPrem, close);
        }}
        startIcon={<SvgDelete />}
      >
        Delete
      </MenuItem>,
    ],
    [onEditOnPremClick, onDeleteOnPremClick],
  );

  const expandables = useMemo(
    () =>
      onPremises.map((onPrem) => (
        <OnPremClientBlock
          key={onPrem.id}
          onPrem={onPrem}
          telemetry={telemetryByClientName[onPrem.mqttClientId]}
          isTelemetryAvailable={isOnPremTelemetryAvailable}
          menuItems={dropdownOnPremMenuItems(onPrem)}
        >
          <div className="horizontal becs-tb-margin">
            <Button
              disabled={iTwinId === undefined}
              as="button"
              onClick={() => {
                setFormState(CredentialFormState.OnPremSystem);
                setSelectedOnPrem(onPrem);
                setFrollUrl("credentials");
              }}
              styleType="high-visibility"
            >
              <div>New on premise system</div>
            </Button>
          </div>
          <Table
            columns={columnsForOnPremSystems}
            emptyTableContent="No data."
            data={onPremSystemsData(onPrem)}
            isLoading={isLoading}
            style={{
              width: `calc(100vw - ${sideMenuWidth}px)`,
            }}
            isSortable={true}
            isResizable={false}
            density="extra-condensed"
            styleType="zebra-rows"
            paginatorRenderer={paginator}
            pageSize={25}
            initialState={{
              sortBy: [{ id: "lastChange", desc: true }],
            }}
            isSelectable={true}
            selectionMode="single"
          />
        </OnPremClientBlock>
      )),
    [
    onPremises,
    telemetryByClientName,
    isOnPremTelemetryAvailable,
    columnsForOnPremSystems,
    dropdownOnPremMenuItems,
    iTwinId,
    isLoading,
    onPremSystemsData,
    paginator,
    sideMenuWidth,
    ],
  );

  const allHeadersHeight =
    mainMenuHeight +
    headerDivHeight +
    headerButtonsDivHeight +
    variantButtonsDivHeight +
    projectSelectorDivHeight ;

  return (
    <div style={{ height: "100%" }}>
      <InformationPanelWrapper
        style={{
          height: "100%",
          maxWidth: "100%",
          borderRadius: 0,
        }}
      >
        <div>
          {fRollUrl !== undefined && <div className="overlay" />}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              style={
                fRollUrl !== undefined
                  ? { pointerEvents: "none", opacity: "0.8" }
                  : {}
              }
            >
              <div className="becs-infopanelbody-footer-line">
                <div className="becs-all-margin" ref={headerDivRef}>
                  <div className="rowWrap">
                    <Button styleType="borderless" onClick={() => { void navigate(-1); }}>
                      <SvgProgressBackward />
                    </Button>
                    <Text variant="title">
                      {`Credentials management${iModelDesc ? ` for ${iModelDesc}` : ""}`}
                    </Text>
                  </div>
                </div>
              </div>

              <div className="becs-ltr-margin">
                <div>
                  {/*iTwin Project Selector - only show when no query param */}
                  {!iTwinIdFromQuery && (
                    <div ref={projectSelectorDivRef}>
                      <div className="horizontal becs-tb-margin becs-t-margin">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <div
                            className="becs-input-label required"
                            style={{ fontWeight: 400 }}
                          >
                            Select iTwin project (showing {projects.length})
                          </div>
                        </div>

                        <ComboBox
                          emptyStateMessage={emptyContent}
                          options={appendLoadMoreOption(selectOptions, !!nextLink)}
                          onChange={(selected: ITwin | typeof LOAD_MORE_VALUE) => {
                            if (selected !== LOAD_MORE_VALUE) {
                              handleProjectSelect(selected);
                            }
                          }}
                          inputProps={{
                            placeholder: searchPlaceholder("Search iTwin project", 3, isProjectsLoading),
                            onChange: projectSearchOnChange,
                          }}
                          filterFunction={(options) => options}
                          value={selectedProject}
                          itemRenderer={paginatedItemRenderer<ITwin>({
                            onLoadMore: fetchNextProjects,
                            isLoading: isProjectsLoading,
                          })}
                        />
                      </div>
                    </div>
                  )}
                  <div
                    className="horizontal becs-tb-margin"
                    ref={headerButtonsDivRef}
                  >
                    <div className="leftAlign">
                      <div className="rowWrap">
                        <Button
                          disabled={iTwinId === undefined}
                          key="newCredentialBtn"
                          as="button"
                          onClick={() => {
                            setFormState(CredentialFormState.Cloud);
                            setFrollUrl("credentials");
                          }}
                          styleType="high-visibility"
                        >
                          <div>New cloud credentials</div>
                        </Button>
                        <Button
                          disabled={iTwinId === undefined}
                          key="newOnPremBtn"
                          as="button"
                          onClick={() => {
                            setFormState(CredentialFormState.OnPremClient);
                            setFrollUrl("credentials");
                          }}
                          styleType="high-visibility"
                        >
                          <div>New on premise</div>
                        </Button>
                      </div>
                    </div>
                    <div className="rightAlign">
                      <SearchBox
                        style={{ width: "15em" }}
                        inputProps={{
                          placeholder: "Search...",
                          onChange: searchBoxOnChange,
                        }}
                      />
                    </div>
                  </div>
                  <div className="rowWrapNoGap" ref={variantButtonsDivRef}>
                    <IconButton
                      styleType="borderless"
                      isActive={!isOnPremVariantActive}
                      label="Cloud"
                      onClick={() => {
                        setIsOnPremVariantActive(false);
                      }}
                    >
                      Cloud
                    </IconButton>
                    <IconButton
                      styleType="borderless"
                      isActive={isOnPremVariantActive}
                      label="On Premise"
                      onClick={() => {
                        setIsOnPremVariantActive(true);
                      }}
                    >
                      On Premise
                    </IconButton>
                  </div>
                </div>
                <div
                  style={{
                    maxWidth: `calc(100vw - ${sideMenuWidth}px)`,
                  }}
                >
                  {!isOnPremVariantActive && (
                    <Table
                      columns={columnsForCloud}
                      emptyTableContent="No data."
                      data={cloudSystemsData}
                      isLoading={isLoading}
                      style={{
                        height: `calc(100vh - ${allHeadersHeight}px)`,
                        width: `calc(100vw - ${sideMenuWidth}px)`,
                      }}
                      isSortable={true}
                      isResizable={false}
                      density="extra-condensed"
                      styleType="zebra-rows"
                      paginatorRenderer={paginator}
                      pageSize={25}
                      initialState={{
                        sortBy: [{ id: "lastChange", desc: true }],
                      }}
                      isSelectable={true}
                      selectionMode="single"
                    />
                  )}
                  {isOnPremVariantActive && (
                    <div
                      style={{
                        background: "var(--iui-color-background)",
                        height: `calc(100vh - ${allHeadersHeight}px)`,
                        width: `calc(100vw - ${sideMenuWidth}px)`,
                        overflowY: "auto",
                      }}
                    >
                      {isLoading ? (
                        <div
                          className="becs-ltr-margin"
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <ProgressRadial indeterminate />
                        </div>
                      ) : (
                        expandables
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {fRollUrl !== undefined && (
          <InformationPanel
            resizable
            isOpen={fRollUrl !== undefined}
            style={{
              width: "40%",
              maxWidth: "70%",
              minWidth: "10%",
              zIndex: getZindex(),
            }}
            className="iui-infoPanel"
          >
            <InformationPanelHeader
              style={{
                padding: 0,
              }}
            >
              <div className="becs-all-margin">
                <Text variant="title">{infoPanelHeader}</Text>
              </div>
            </InformationPanelHeader>
            <InformationPanelBody style={{ padding: 0 }}>
              <InformationPanelContent
                displayStyle="default"
                style={{ padding: 0, height: "100%" }}
              >
                {(() => {
                  switch (formState) {
                    case CredentialFormState.OnPremSystem:
                      return (
                        <OnPremSystemForm
                          iTwinId={effectiveITwinId as string}
                          isStandaloneMode={isStandaloneMode}
                          onPremiseId={selectedOnPrem?.id as string}
                          isFromUrl={false}
                          params={getParamsFromFrollNavUrl}
                          closeFrollFunction={onFrollClose}
                          navigateToInformationPanelFunction={
                            navigateToInfPanelFunction
                          }
                          setInforPanelHeaderFunction={onSetInfoPanelHeader}
                        />
                      );
                    case CredentialFormState.Cloud:
                      return (
                        <CredentialsForm
                          iTwinId={effectiveITwinId as string}
                          isStandaloneMode={isStandaloneMode}
                          system={selectedSystem}
                          isFromUrl={false}
                          params={getParamsFromFrollNavUrl}
                          closeFrollFunction={onFrollClose}
                          navigateToInformationPanelFunction={
                            navigateToInfPanelFunction
                          }
                          setInforPanelHeaderFunction={onSetInfoPanelHeader}
                        />
                      );
                    case CredentialFormState.OnPremClient:
                      return (
                        <OnPremForm
                          iTwinId={effectiveITwinId as string}
                          isStandaloneMode={isStandaloneMode}
                          onPrem={selectedOnPrem}
                          isFromUrl={false}
                          params={getParamsFromFrollNavUrl}
                          closeFrollFunction={onFrollClose}
                          navigateToInformationPanelFunction={
                            navigateToInfPanelFunction
                          }
                          setInforPanelHeaderFunction={onSetInfoPanelHeader}
                        />
                      );
                    default:
                      return null;
                  }
                })()}
              </InformationPanelContent>
            </InformationPanelBody>
          </InformationPanel>
        )}
      </InformationPanelWrapper>
    </div>
  );
};

export default CredentialsList;
